import React from 'react';
import { motion } from 'framer-motion';
import Badge from '../ui/Badge';
import { User, Hash, Layers } from 'lucide-react';

const keyframeCSS = `
  @media (max-width: 600px) {
    .gh-container { padding: 8px 12px !important; gap: 8px !important; }
    .gh-left { gap: 8px !important; }
    .gh-info-group { gap: 6px !important; }
    .gh-info-item { font-size: 0.72rem !important; padding: 3px 8px !important; }
    .gh-right { gap: 8px !important; }
    .gh-round-seg { width: 14px !important; height: 6px !important; }
    .gh-round-label { display: none !important; }
  }
  @keyframes headerGoldShimmer {
    0% { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes headerBorderGlow {
    0%, 100% { opacity: 0.3; }
    50% { opacity: 0.7; }
  }
  @keyframes headerSegmentPulse {
    0%, 100% { box-shadow: inset 0 0 4px rgba(212,175,55,0.3); }
    50% { box-shadow: inset 0 0 8px rgba(212,175,55,0.6), 0 0 4px rgba(212,175,55,0.2); }
  }
  @keyframes statusGlow {
    0%, 100% { box-shadow: 0 0 6px var(--glow-color, rgba(212,175,55,0.3)); }
    50% { box-shadow: 0 0 14px var(--glow-color, rgba(212,175,55,0.5)), 0 0 24px var(--glow-color, rgba(212,175,55,0.15)); }
  }
`;

const statusMap = {
  setup: { label: 'Preparazione', variant: 'info', glowColor: 'rgba(52,152,219,0.4)' },
  playing: { label: 'In gioco', variant: 'success', glowColor: 'rgba(46,204,113,0.4)' },
  offer_pending: { label: 'Offerta', variant: 'warning', glowColor: 'rgba(243,156,18,0.4)' },
  completed: { label: 'Completata', variant: 'default', glowColor: 'rgba(160,160,192,0.3)' },
  cancelled: { label: 'Annullata', variant: 'danger', glowColor: 'rgba(231,76,60,0.4)' },
};

export default function GameHeader({ game }) {
  if (!game) return null;

  const status = statusMap[game.status] || { label: game.status, variant: 'default', glowColor: 'rgba(160,160,192,0.3)' };
  const totalRounds = 6;
  const currentRound = game.current_round || 1;

  const containerStyle = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    padding: '12px 28px',
    background: 'linear-gradient(180deg, rgba(12,12,35,0.98) 0%, rgba(8,8,25,0.95) 100%)',
    position: 'relative',
    flexWrap: 'wrap',
    gap: '12px',
    borderBottom: 'none',
  };

  // Animated gradient border at bottom
  const borderBottomStyle = {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: 0,
    height: '2px',
    background: 'linear-gradient(90deg, transparent, #D4AF37, #F0D060, #D4AF37, transparent)',
    backgroundSize: '200% 100%',
    animation: 'headerBorderGlow 3s ease-in-out infinite',
  };

  const leftStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '24px',
  };

  const titleStyle = {
    fontFamily: 'var(--font-display)',
    fontSize: 'clamp(1.2rem, 3vw, 1.6rem)',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
    margin: 0,
    background: 'linear-gradient(90deg, #B8960C, #D4AF37, #F0D060, #D4AF37, #B8960C)',
    backgroundSize: '200% auto',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    animation: 'headerGoldShimmer 4s linear infinite',
    filter: 'drop-shadow(0 0 12px rgba(212,175,55,0.3))',
  };

  const infoGroupStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const infoItemStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
    fontSize: '0.82rem',
    fontFamily: 'var(--font-body)',
    color: 'rgba(200,200,220,0.8)',
    padding: '4px 12px',
    background: 'rgba(255,255,255,0.04)',
    borderRadius: '9999px',
    border: '1px solid rgba(255,255,255,0.06)',
  };

  const infoIconStyle = {
    color: '#D4AF37',
    opacity: 0.7,
  };

  // Round indicator segments
  const roundContainerStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '4px',
  };

  const rightStyle = {
    display: 'flex',
    alignItems: 'center',
    gap: '16px',
  };

  const statusBadgeStyle = {
    borderRadius: '9999px',
  };

  return (
    <div className="gh-container" style={containerStyle}>
      <style>{keyframeCSS}</style>

      {/* Animated bottom border */}
      <div style={borderBottomStyle} />

      {/* Left section */}
      <div className="gh-left" style={leftStyle}>
        <motion.h1
          style={titleStyle}
          initial={{ opacity: 0, x: -20 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.5 }}
        >
          PICKBOXGAME
        </motion.h1>

        <div className="gh-info-group" style={infoGroupStyle}>
          {game.player_name && (
            <motion.div
              className="gh-info-item"
              style={infoItemStyle}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
            >
              <User size={14} style={infoIconStyle} />
              <span>{game.player_name}</span>
            </motion.div>
          )}
          {game.player_box_number && (
            <motion.div
              className="gh-info-item"
              style={infoItemStyle}
              initial={{ opacity: 0, y: -8 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
            >
              <Hash size={14} style={infoIconStyle} />
              <span>Pacco {game.player_box_number}</span>
            </motion.div>
          )}
        </div>
      </div>

      {/* Right section */}
      <div className="gh-right" style={rightStyle}>
        {/* Round indicator as illuminated segments */}
        <div style={roundContainerStyle}>
          <span className="gh-round-label" style={{
            fontFamily: 'var(--font-display)',
            fontSize: '0.7rem',
            color: 'rgba(200,200,220,0.5)',
            letterSpacing: '0.08em',
            textTransform: 'uppercase',
            marginRight: '6px',
          }}>
            Round
          </span>
          {Array.from({ length: totalRounds }, (_, i) => {
            const roundNum = i + 1;
            const isActive = roundNum <= currentRound;
            const isCurrent = roundNum === currentRound;

            const segmentStyle = {
              width: '22px',
              height: '8px',
              borderRadius: '2px',
              background: isActive
                ? 'linear-gradient(135deg, #D4AF37, #F0D060)'
                : 'rgba(60,60,90,0.4)',
              border: isActive
                ? '1px solid rgba(212,175,55,0.5)'
                : '1px solid rgba(80,80,110,0.3)',
              transition: 'all 0.3s ease',
              animation: isCurrent ? 'headerSegmentPulse 2s ease-in-out infinite' : 'none',
              boxShadow: isActive
                ? '0 0 6px rgba(212,175,55,0.3)'
                : 'none',
            };

            return <div key={i} className="gh-round-seg" style={segmentStyle} />;
          })}
        </div>

        {/* Status badge with glow */}
        <motion.div
          style={statusBadgeStyle}
          initial={{ opacity: 0, scale: 0.8 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3 }}
        >
          <Badge variant={status.variant}>{status.label}</Badge>
        </motion.div>
      </div>
    </div>
  );
}
